export type Fetcher = (url: string, options?: RequestInit) => Promise<Response>;

export type NotificationPushPreference = {
  enabled: boolean;
  pushAvailable: boolean;
  availabilityReason: string | null;
  activeSubscriptionCount: number;
  hasActiveSubscription: boolean;
  webPushConfigured: boolean;
};

export type NotificationPreferences = {
  notifications: {
    strategy_action_required: {
      push: NotificationPushPreference;
    };
  };
};

const parseErrorMessage = async (response: Response): Promise<string> => {
  try {
    const payload = await response.json();
    if (typeof payload?.message === 'string') return payload.message;
    if (typeof payload?.error === 'string') return payload.error;
  } catch {
    // fall through
  }
  return `Request failed with status ${response.status}`;
};

const fetchJson = async <T>(url: string, init?: RequestInit, fetcher?: Fetcher): Promise<T> => {
  const executeFetch = fetcher ?? fetch;
  const response = await executeFetch(url, init);
  if (!response.ok) {
    throw new Error(await parseErrorMessage(response));
  }
  return response.json() as Promise<T>;
};

export const fetchNotificationPreferences = async (fetcher?: Fetcher): Promise<NotificationPreferences> =>
  fetchJson<NotificationPreferences>('/api/me/notifications/preferences', undefined, fetcher);

export const updateNotificationPreference = async (
  input: {
    notificationCategory: 'strategy_action_required';
    channel: 'push';
    enabled: boolean;
  },
  fetcher?: Fetcher
): Promise<NotificationPreferences> =>
  fetchJson<NotificationPreferences>(
    '/api/me/notifications/preferences',
    {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        notification_category: input.notificationCategory,
        channel: input.channel,
        enabled: input.enabled,
      }),
    },
    fetcher
  );
